import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Palette, Upload, Eye, Save, Image as ImageIcon, 
  Building2, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TenantBranding() {
  const { t } = useI18n();
  const [branding, setBranding] = useState({
    company_name: '',
    logo_url: '',
    primary_color: '#3b82f6',
    favicon_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const fileInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  const fetchBranding = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/branding/tenant/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranding({
        company_name: response.data.company_name || '',
        logo_url: response.data.logo_url || '',
        primary_color: response.data.primary_color || '#3b82f6',
        favicon_url: response.data.favicon_url || ''
      });
    } catch (error) {
      toast.error('Failed to fetch branding');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/branding/tenant/config`, branding, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branding saved successfully');
      
      // Update local storage tenant name
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
      if (branding.company_name) {
        tenant.name = branding.company_name;
        localStorage.setItem('tenant', JSON.stringify(tenant));
      }
    } catch (error) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/branding/tenant/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBranding(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 512 * 1024) { toast.error('Favicon must be less than 512KB'); return; }

    setUploadingFavicon(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/branding/tenant/logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      // The upload endpoint returns logo_url, we'll use the same base64 approach for favicon
      setBranding(prev => ({ ...prev, favicon_url: response.data.logo_url }));
      toast.success('Favicon uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('branding.title', 'Branding Settings')}</h1>
        <p className="text-slate-500 mt-1">Customize your organization's appearance</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Form */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Palette className="h-5 w-5 text-blue-600" />
              Branding Options
            </CardTitle>
            <CardDescription>Update your organization's branding</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Name */}
            <div>
              <Label className="text-slate-700">Company Name</Label>
              <Input
                value={branding.company_name}
                onChange={(e) => setBranding(prev => ({ ...prev, company_name: e.target.value }))}
                className="mt-2"
                placeholder="Your Company Name"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <Label className="text-slate-700">Company Logo</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center overflow-hidden bg-slate-50">
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-slate-400" />
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">Max 2MB, PNG or JPG</p>
                </div>
              </div>
            </div>

            {/* Primary Color */}
            <div>
              <Label className="text-slate-700">Primary Color</Label>
              <div className="mt-2 flex items-center gap-4">
                <input
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-12 h-12 rounded cursor-pointer border-0"
                />
                <Input
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-32"
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            {/* Favicon Upload */}
            <div>
              <Label className="text-slate-700">Favicon</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-12 h-12 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center overflow-hidden bg-slate-50">
                  {branding.favicon_url ? (
                    <img src={branding.favicon_url} alt="Favicon" className="w-full h-full object-contain" />
                  ) : (
                    <ImageIcon className="h-5 w-5 text-slate-400" />
                  )}
                </div>
                <div>
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFaviconUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={uploadingFavicon}
                  >
                    <Upload className="mr-2 h-3.5 w-3.5" />
                    {uploadingFavicon ? 'Uploading...' : 'Upload Favicon'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">Max 512KB, 32x32 or 64x64 recommended</p>
                </div>
              </div>
            </div>

            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Preview
            </CardTitle>
            <CardDescription>How your branding will appear</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Login Preview */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="p-6 bg-white">
                <div className="flex items-center justify-center gap-3 mb-4">
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Logo" className="h-12 w-12 object-contain" />
                  ) : (
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: branding.primary_color + '20' }}
                    >
                      <Building2 className="h-6 w-6" style={{ color: branding.primary_color }} />
                    </div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-center text-slate-900">
                  {branding.company_name || 'Your Company'}
                </h3>
                <p className="text-slate-500 text-center text-sm mt-1">Sign in to continue</p>
                
                <div className="mt-4 space-y-3">
                  <div className="h-10 bg-slate-100 rounded" />
                  <div className="h-10 bg-slate-100 rounded" />
                  <button 
                    className="w-full h-10 rounded text-white font-medium"
                    style={{ backgroundColor: branding.primary_color }}
                  >
                    Sign In
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar Preview */}
            <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
              <div 
                className="p-4"
                style={{ backgroundColor: branding.primary_color }}
              >
                <div className="flex items-center gap-3">
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Logo" className="h-8 w-8 object-contain bg-white rounded" />
                  ) : (
                    <div className="p-2 bg-white/20 rounded">
                      <Building2 className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <span className="text-white font-bold">
                    {branding.company_name || 'Your Company'}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-slate-900">
                <div className="space-y-2">
                  {['Dashboard', 'Positions', 'Resumes'].map((item) => (
                    <div 
                      key={item}
                      className="px-3 py-2 rounded text-slate-400 text-sm hover:bg-slate-800"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Button Preview */}
            <div className="mt-4 p-4 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-500 mb-3">Button Styles</p>
              <div className="flex gap-3 flex-wrap">
                <button 
                  className="px-4 py-2 rounded text-white font-medium"
                  style={{ backgroundColor: branding.primary_color }}
                >
                  Primary
                </button>
                <button 
                  className="px-4 py-2 rounded font-medium border-2"
                  style={{ borderColor: branding.primary_color, color: branding.primary_color }}
                >
                  Secondary
                </button>
                <button 
                  className="px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: branding.primary_color + '20', color: branding.primary_color }}
                >
                  Tertiary
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
